import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '../../../components/ui/date-picker';
import { Dialog, DialogApply } from '../../../components/ui/dialog';

/**
 * Xác nhận sau khi kéo một **mốc** sang ngày khác — APM-GANTT-d.
 *
 * ## Vì sao mốc phải hỏi lại, còn thanh việc thì không
 *
 * Kéo một thanh việc là chuyện xảy ra hàng chục lần trong một buổi lập kế
 * hoạch, và sai thì kéo lại. Một mốc thì khác: nó là **ngày đã cam kết với bên
 * ngoài** — bàn giao, nghiệm thu, mở bán. Dời nó là dời một lời hứa, và một cú
 * trượt tay vài pixel không nên làm được điều đó trong im lặng.
 *
 * ## Vì sao hiện cả ngày cũ lẫn ngày mới
 *
 * Sau khi thả tay, hình thoi đã nằm ở chỗ mới — nên màn hình không còn chỗ nào
 * nói ngày cũ là ngày nào. Người dùng cần đối chiếu để biết mình vừa dời **bao
 * xa**, và đó là thứ quyết định họ bấm xác nhận hay huỷ.
 *
 * ## Vì sao ô ngày sửa được
 *
 * Kéo thả cho ra ngày *gần đúng*: ở mức thu nhỏ (`month`, 4px/ngày) thì một
 * pixel là một phần tư ngày, nên trượt đúng vào ngày định nhắm gần như bất khả.
 * Ô nhập biến "kéo tới gần rồi sửa cho đúng" thành một thao tác, thay vì kéo đi
 * kéo lại.
 */
export function MilestoneMoveDialog({
  title,
  from,
  to,
  onCancel,
  onConfirm,
}: {
  readonly title: string;
  /** Ngày cũ, `YYYY-MM-DD`. */
  readonly from: string;
  /** Ngày kéo tới — giá trị khởi tạo của ô nhập. */
  readonly to: string;
  readonly onCancel: () => void;
  readonly onConfirm: (date: string) => void;
}): ReactNode {
  const { t } = useTranslation();
  const [date, setDate] = useState(to);

  return (
    <Dialog label={t('gantt.moveMilestoneTitle')} onClose={onCancel}>
      <div className="flex flex-col gap-3">
        <h2 className="text-sm font-medium">{t('gantt.moveMilestoneTitle')}</h2>
        <p className="text-sm text-muted-foreground">{title}</p>

        <dl className="flex items-center gap-2 text-sm">
          <dt className="text-muted-foreground">{t('gantt.moveMilestoneFrom')}</dt>
          {/* Gạch ngang: ngày cũ là thứ **đang bị thay**, không phải một lựa chọn. */}
          <dd className="tabular-nums line-through">{from}</dd>
          <span aria-hidden>→</span>
          <dt className="sr-only">{t('gantt.moveMilestoneTo')}</dt>
          <dd>
            <DatePicker value={date} onChange={setDate} />
          </dd>
        </dl>

        <DialogApply
          label={t('gantt.moveMilestoneConfirm')}
          // Ô trống thì không có gì để lưu; quay về ngày cũ thì cũng vậy, và
          // gửi một lời gọi không đổi gì chỉ thêm một dòng vào `event_log`.
          disabled={date === '' || date === from}
          onClick={() => onConfirm(date)}
        />
      </div>
    </Dialog>
  );
}
